/**
 * Client API pour @ollaid/native-sso
 * Gestion des requêtes HTTP avec timeout, device ID, token storage
 *
 * @version 2.7.9
 */

import * as CryptoJS from 'crypto-js';
import { logApiCall, logApiResponse } from './debugLogger';

// ============================================
// Types d'erreur
// ============================================

export type ApiErrorType = 'network' | 'timeout' | 'server' | 'validation' | 'auth' | 'unknown';

export class ApiError extends Error {
  type: ApiErrorType;
  statusCode?: number;
  errorType?: string;
  response?: unknown;

  constructor(
    message: string,
    type: ApiErrorType,
    statusCode?: number,
    errorType?: string,
    response?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
    this.type = type;
    this.statusCode = statusCode;
    this.errorType = errorType;
    this.response = response;
  }
}

// ============================================
// Configuration
// ============================================

export interface NativeAuthConfig {
  /** URL du Backend SaaS (pour config et exchange) */
  saasApiUrl: string;
  /** URL du Backend IAM (pour encrypt, init, validate, etc.) */
  iamApiUrl: string;
  /** Timeout en ms (défaut: 30000) */
  timeout?: number;
  /** Mode debug (logs console) */
  debug?: boolean;
  /** Bypass du flux needs_access, déterminé par le backend */
  bypass?: boolean;
  /**
   * Préfixe de configuration lu côté SaaS pour charger les credentials.
   * Ne doit pas être envoyé au backend IAM natif.
   */
  configPrefix?: string;
  /**
   * Stockage session injectable.
   * Défaut: storage navigateur chiffré, mémoire en fallback.
   * Utile pour Capacitor ou des wrappers secure-storage côté app consommatrice.
   */
  storage?: NativeStorageAdapter;
}

let config: NativeAuthConfig = {
  saasApiUrl: '',
  iamApiUrl: '',
  timeout: 30000,
  debug: false,
  bypass: false,
  configPrefix: 'iam',
};

export interface NativeStorageAdapter {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  clear(): void;
}

const memoryStorage = (): NativeStorageAdapter => {
  const state = new Map<string, string>();
  return {
    getItem: (key) => (state.has(key) ? state.get(key)! : null),
    setItem: (key, value) => {
      state.set(key, value);
    },
    removeItem: (key) => {
      state.delete(key);
    },
    clear: () => {
      state.clear();
    },
  };
};

const STORAGE_ENCRYPTION_PREFIX = 'enc:v1:';
const STORAGE_ENCRYPTION_SECRET_PREFIX = 'ollaid-native-sso::storage::v1';
const DEBUG_REDACTED = '[redacted]';

const RAW_STORAGE_KEYS = new Set<string>([
  'sso_storage_seed',
]);

const STORAGE_PREFIX = 'sso_';

function resolveBaseStorage(): NativeStorageAdapter {
  if (typeof localStorage !== 'undefined') {
    return {
      getItem: (key) => localStorage.getItem(key),
      setItem: (key, value) => localStorage.setItem(key, value),
      removeItem: (key) => localStorage.removeItem(key),
      clear: () => localStorage.clear(),
    };
  }
  return memoryStorage();
}

let rawStorageAdapter: NativeStorageAdapter = resolveBaseStorage();
let storageAdapter: NativeStorageAdapter = createEncryptedStorageAdapter(rawStorageAdapter);

function isRawStorageKey(key: string): boolean {
  return RAW_STORAGE_KEYS.has(key);
}

function normalizeStorageValue(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value === null || value === undefined) return '';
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return String(value);
  }

  try {
    const serialized = JSON.stringify(value);
    return serialized ?? '';
  } catch {
    return String(value);
  }
}

function safeJSONStringify(value: unknown): string {
  try {
    const serialized = JSON.stringify(value);
    return serialized ?? 'null';
  } catch {
    return 'null';
  }
}

function isSensitiveDebugKey(key: string): boolean {
  const normalized = key.toLowerCase();
  return (
    normalized.includes('token') ||
    normalized.includes('password') ||
    normalized.includes('secret') ||
    normalized.includes('authorization') ||
    normalized.includes('otp') ||
    normalized.includes('code') ||
    normalized.includes('cookie') ||
    normalized.includes('credential') ||
    normalized.includes('app_access') ||
    normalized.includes('sanctum') ||
    normalized.includes('refresh')
  );
}

function sanitizeDebugValue(value: unknown, keyHint = ''): unknown {
  if (value === null || value === undefined) return value;
  if (typeof value === 'string') {
    if (keyHint && isSensitiveDebugKey(keyHint)) return DEBUG_REDACTED;
    if (/^Bearer\s+/i.test(value)) return DEBUG_REDACTED;
    if (/^[0-9]+\|[A-Za-z0-9]/.test(value)) return DEBUG_REDACTED;
    return value;
  }
  if (typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map(item => sanitizeDebugValue(item));

  const input = value as Record<string, unknown>;
  const output: Record<string, unknown> = {};
  for (const [key, child] of Object.entries(input)) {
    output[key] = isSensitiveDebugKey(key) ? DEBUG_REDACTED : sanitizeDebugValue(child, key);
  }
  return output;
}

function buildDebugSummary(responseBody: unknown): Record<string, unknown> {
  if (!responseBody || typeof responseBody !== 'object') {
    return {};
  }

  const payload = responseBody as Record<string, unknown>;
  const summary: Record<string, unknown> = {};

  if (typeof payload.success === 'boolean') summary.success = payload.success;
  if (typeof payload.status === 'string') summary.status = payload.status;
  if (typeof payload.error_type === 'string') summary.error_type = payload.error_type;
  if (typeof payload.message === 'string') summary.message = payload.message;
  if (typeof payload.needs_access === 'boolean') summary.needs_access = payload.needs_access;
  if (typeof payload.requires_2fa === 'boolean') summary.requires_2fa = payload.requires_2fa;
  if (typeof payload.callback_token === 'string') summary.has_callback_token = true;
  if (typeof payload.process_token === 'string') summary.has_process_token = true;
  if (typeof payload.otp_code === 'string') summary.has_otp_code = true;
  if (typeof payload.otp_method === 'string') summary.otp_method = payload.otp_method;
  if (typeof payload.otp_sent_to === 'string') summary.otp_sent_to = payload.otp_sent_to;
  if (typeof payload.cooldown_remaining === 'number') summary.cooldown_remaining = payload.cooldown_remaining;
  if (typeof payload.wait_seconds === 'number') summary.wait_seconds = payload.wait_seconds;

  return summary;
}

function logDebugApiResponse(
  method: string,
  url: string,
  status: number,
  responseBody: unknown,
  duration: number,
  requestBody?: unknown,
  error?: string
): void {
  if (!isDebugMode()) return;

  const title = `${status >= 200 && status < 300 ? '✅' : '❌'} [native-sso] ${method} ${url}`;
  const summary = buildDebugSummary(responseBody);
  const safeRequest = sanitizeDebugValue(requestBody);
  const safeResponse = sanitizeDebugValue(responseBody);

  try {
    console.groupCollapsed(title);
    console.log({
      status,
      duration: `${duration}ms`,
      summary,
      request: safeRequest,
      response: safeResponse,
      error: error || undefined,
    });
    console.groupEnd();
  } catch {
    // console unavailable or partially supported
  }
}

function getStorageEncryptionSecret(): string {
  const storage = rawStorageAdapter;
  let seed = storage.getItem(STORAGE_SEED_KEY);

  if (!seed) {
    seed = `seed_${Date.now()}_${Math.random().toString(36).substring(2, 15)}_${Math.random().toString(36).substring(2, 15)}`;
    storage.setItem(STORAGE_SEED_KEY, seed);
  }

  const origin = typeof window !== 'undefined' && window.location?.origin
    ? window.location.origin
    : 'unknown-origin';
  const prefix = config.configPrefix || 'iam';
  return `${STORAGE_ENCRYPTION_SECRET_PREFIX}::${origin}::${prefix}::${seed}`;
}

function encryptStorageValue(value: unknown): string {
  const normalizedValue = normalizeStorageValue(value);

  try {
    const secret = getStorageEncryptionSecret();
    const ciphertext = CryptoJS.AES.encrypt(normalizedValue, secret).toString();
    return `${STORAGE_ENCRYPTION_PREFIX}${ciphertext}`;
  } catch (error) {
    if (isDebugMode()) {
      console.warn('⚠️ [native-sso] Encryption storage fallback to plain value', error);
    }
    return normalizedValue;
  }
}

function decryptStorageValue(value: unknown): string | null {
  const normalizedValue = normalizeStorageValue(value);

  if (!normalizedValue.startsWith(STORAGE_ENCRYPTION_PREFIX)) {
    return normalizedValue;
  }

  const ciphertext = normalizedValue.slice(STORAGE_ENCRYPTION_PREFIX.length);
  if (!ciphertext) return null;

  try {
    const secret = getStorageEncryptionSecret();
    const bytes = CryptoJS.AES.decrypt(ciphertext, secret);
    const plaintext = bytes.toString(CryptoJS.enc.Utf8);
    return plaintext || null;
  } catch (error) {
    if (isDebugMode()) {
      console.warn('⚠️ [native-sso] Failed to decrypt storage value, clearing corrupted entry', error);
    }
    return null;
  }
}

function createEncryptedStorageAdapter(base: NativeStorageAdapter): NativeStorageAdapter {
  return {
    getItem: (key) => {
      const rawValue = base.getItem(key);
      if (rawValue !== null) {
        if (isRawStorageKey(key)) {
          return rawValue;
        }

        const decrypted = decryptStorageValue(rawValue);
        if (decrypted !== null) {
          return decrypted;
        }

        if (normalizeStorageValue(rawValue).startsWith(STORAGE_ENCRYPTION_PREFIX)) {
          base.removeItem(key);
          return null;
        }

        return rawValue;
      }
      return null;
    },
    setItem: (key, value) => {
      if (isRawStorageKey(key)) {
        base.setItem(key, normalizeStorageValue(value));
        return;
      }

      base.setItem(key, encryptStorageValue(value));
    },
    removeItem: (key) => {
      base.removeItem(key);
    },
    clear: () => {
      base.clear();
    },
  };
}

export function setNativeStorage(adapter?: NativeStorageAdapter): void {
  rawStorageAdapter = adapter ?? resolveBaseStorage();
  storageAdapter = createEncryptedStorageAdapter(rawStorageAdapter);
}

export function getNativeStorage(): NativeStorageAdapter {
  return storageAdapter;
}

export const setNativeAuthConfig = (newConfig: Partial<NativeAuthConfig>): void => {
  config = { ...config, ...newConfig };
  if (newConfig.storage) {
    setNativeStorage(newConfig.storage);
  } else if (!config.storage) {
    setNativeStorage();
  }
  
  if (config.debug) {
    console.log('🔐 @ollaid/native-sso Config:', {
      saasApiUrl: config.saasApiUrl,
      iamApiUrl: config.iamApiUrl,
    });
  }
};

export const getNativeAuthConfig = (): NativeAuthConfig => ({ ...config });

export const isDebugMode = (): boolean => config.debug === true;

export const getSaasApiBaseUrl = (): string => {
  if (!config.saasApiUrl) {
    throw new ApiError('saasApiUrl non configurée', 'unknown');
  }
  return config.saasApiUrl;
};

export const getIamApiBaseUrl = (): string => {
  if (!config.iamApiUrl) {
    throw new ApiError('iamApiUrl non configurée', 'unknown');
  }
  return config.iamApiUrl;
};

// ============================================
// Device ID
// ============================================

const DEVICE_ID_KEY = 'sso_device_id';
const SESSION_UUID_KEY = 'sso_session_uuid';
const STORAGE_SEED_KEY = 'native_sso_storage_seed';

function generateUuid(): string {
  // Prefer native UUID when available (modern browsers / webviews).
  // Fallback is not cryptographically strong, but good enough as a stable instance id.
  const globalCrypto = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
  if (globalCrypto && typeof globalCrypto.randomUUID === 'function') {
    return globalCrypto.randomUUID();
  }
  return `uuid_${Date.now()}_${Math.random().toString(36).slice(2)}_${Math.random().toString(36).slice(2)}`;
}

export const getDeviceId = (): string => {
  const storage = getNativeStorage();
  let deviceId = storage.getItem(DEVICE_ID_KEY);
  if (!deviceId) {
    deviceId = `nat_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    storage.setItem(DEVICE_ID_KEY, deviceId);
  }
  return deviceId;
};

/**
 * Stable session UUID (instance id) persisted in storage.
 *
 * This is NOT a security token. It is used to disambiguate sessions across devices/webviews
 * and to help backends implement multi-session behavior safely.
 */
export const getSessionUuid = (): string => {
  const storage = getNativeStorage();
  let uuid = storage.getItem(SESSION_UUID_KEY);
  if (!uuid) {
    uuid = generateUuid();
    storage.setItem(SESSION_UUID_KEY, uuid);
  }
  return uuid;
};

// ============================================
// Device context (Capacitor)
// ============================================

export interface NativeDeviceContext {
  deviceName?: string | null;
  deviceModel?: string | null;
  deviceManufacturer?: string | null;
  operatingSystem?: string | null;
  osVersion?: string | null;
  platform?: string | null;
  browser?: string | null;
  language?: string | null;
  webviewVersion?: string | null;
  isNative?: boolean | null;
  label?: string | null;
}

let deviceContext: NativeDeviceContext | null = null;
let deviceContextPromise: Promise<NativeDeviceContext | null> | null = null;

function inferBrowserFromUserAgent(userAgent?: string | null): string | null {
  if (!userAgent) return null;
  if (userAgent.includes('Firefox')) return 'Firefox';
  if (userAgent.includes('Edg')) return 'Edge';
  if (userAgent.includes('Chrome')) return 'Chrome';
  if (userAgent.includes('Safari')) return 'Safari';
  if (userAgent.includes('Opera') || userAgent.includes('OPR')) return 'Opera';
  return null;
}

function buildDeviceLabel(context: NativeDeviceContext): string {
  const parts = [
    context.deviceName,
    context.deviceManufacturer,
    context.deviceModel,
    context.operatingSystem && context.osVersion
      ? `${context.operatingSystem} ${context.osVersion}`
      : context.operatingSystem,
  ].filter(Boolean) as string[];

  if (parts.length > 0) {
    return parts.join(' ');
  }

  if (context.browser || context.platform) {
    return `${context.browser || 'Appareil'} - ${context.platform || 'mobile'}`;
  }

  return 'Appareil mobile';
}

async function loadDeviceContext(): Promise<NativeDeviceContext | null> {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const { Device } = await import('@capacitor/device');
    const info = await Device.getInfo();
    const languageResult = await Device.getLanguageCode().catch(() => null) as { value?: string } | null;
    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : null;
    const context: NativeDeviceContext = {
      deviceName: info.name ?? null,
      deviceModel: info.model ?? null,
      deviceManufacturer: info.manufacturer ?? null,
      operatingSystem: info.operatingSystem ?? null,
      osVersion: info.osVersion ?? null,
      platform: info.platform ?? null,
      browser: inferBrowserFromUserAgent(userAgent),
      language: languageResult?.value ?? (typeof navigator !== 'undefined' ? navigator.language : null),
      webviewVersion: (info as { webViewVersion?: string }).webViewVersion ?? null,
      isNative: info.platform ? info.platform !== 'web' : null,
      label: '',
    };

    context.label = buildDeviceLabel(context);
    deviceContext = context;
    return context;
  } catch {
    return null;
  }
}

export async function ensureDeviceContext(): Promise<NativeDeviceContext | null> {
  if (deviceContext) {
    return deviceContext;
  }

  if (!deviceContextPromise) {
    deviceContextPromise = loadDeviceContext().finally(() => {
      deviceContextPromise = null;
    });
  }

  return deviceContextPromise;
}

// ============================================
// Token storage (clés unifiées avec Web SSO)
// ============================================

const STORAGE = {
  AUTH_TOKEN: 'sso_auth_token',
  TOKEN: 'sso_token',
  USER: 'sso_user',
  REGISTRATION_MODE: 'sso_registration_mode',
  ALIAS_REFERENCE: 'sso_alias_reference',
  REFRESH_TOKEN: 'sso_refresh_token',
  TOKEN_EXPIRES_AT: 'sso_token_expires_at',
  REFRESH_EXPIRES_AT: 'sso_refresh_expires_at',
} as const;

const PROFILE_STORAGE = {
  IMAGE_LAST_STATUS: 'sso_image_last_status',
  IMAGE_LAST_CHECK: 'sso_image_last_check',
  IMAGE_RECHECK_AT: 'sso_image_recheck_at',
} as const;

const ALL_SESSION_STORAGE_KEYS = [
  STORAGE.AUTH_TOKEN,
  STORAGE.TOKEN,
  STORAGE.USER,
  STORAGE.REGISTRATION_MODE,
  STORAGE.ALIAS_REFERENCE,
  STORAGE.REFRESH_TOKEN,
  STORAGE.TOKEN_EXPIRES_AT,
  STORAGE.REFRESH_EXPIRES_AT,
  PROFILE_STORAGE.IMAGE_LAST_STATUS,
  PROFILE_STORAGE.IMAGE_LAST_CHECK,
  PROFILE_STORAGE.IMAGE_RECHECK_AT,
  DEVICE_ID_KEY,
  SESSION_UUID_KEY,
  STORAGE_SEED_KEY,
] as const;

export interface SsoSessionSnapshot<TUser = unknown> {
  authToken: string | null;
  refreshToken: string | null;
  refreshExpiresAt: string | null;
  tokenExpiresAt: string | null;
  iamToken: string | null;
  aliasReference: string | null;
  accountType: string | null;
  deviceId: string | null;
  sessionUuid: string | null;
  user: TUser | null;
}

export const setAuthToken = (token: string): void => {
  const storage = getNativeStorage();
  if (typeof token !== 'string' || token.length === 0) return;
  storage.setItem(STORAGE.AUTH_TOKEN, token);
};

export const getAuthToken = (): string | null => {
  const storage = getNativeStorage();
  return storage.getItem(STORAGE.AUTH_TOKEN) || storage.getItem(STORAGE.TOKEN);
};

export const clearAuthToken = (): void => {
  const storage = getNativeStorage();
  [
    STORAGE.AUTH_TOKEN,
    STORAGE.TOKEN,
    STORAGE.USER,
    STORAGE.REGISTRATION_MODE,
    STORAGE.ALIAS_REFERENCE,
    STORAGE.REFRESH_TOKEN,
    STORAGE.TOKEN_EXPIRES_AT,
    STORAGE.REFRESH_EXPIRES_AT,
  ].forEach((key) => {
    try {
      storage.removeItem(key);
    } catch {
      // best effort cleanup
    }
  });
};

export interface NativeSsoStorageRepairResult {
  cleaned: boolean;
  reason: 'incomplete_session' | 'invalid_user_json' | null;
}

/**
 * Nettoyage complet du storage SSO.
 * 
 * À utiliser quand la session est manifestement corrompue ou incohérente.
 * Par défaut, on retire aussi les identifiants d'instance (device/session UUID)
 * pour repartir sur une base totalement saine.
 */
export const clearNativeSsoStorage = (options?: {
  preserveDeviceIdentity?: boolean;
}): void => {
  const storage = getNativeStorage();
  const preserveDeviceIdentity = options?.preserveDeviceIdentity === true;

  ALL_SESSION_STORAGE_KEYS.forEach((key) => {
    if (preserveDeviceIdentity && (key === DEVICE_ID_KEY || key === SESSION_UUID_KEY || key === STORAGE_SEED_KEY)) {
      return;
    }
    try {
      storage.removeItem(key);
    } catch {
      // best effort cleanup
    }
  });
};

/**
 * Vérifie l'intégrité du storage SSO et purge les états manifestement incohérents.
 * 
 * Retourne `cleaned=true` si une réparation a été effectuée.
 * Cette fonction est volontairement conservatrice: elle ne nettoie que les cas
 * où la session est objectivement inutilisable.
 */
export const repairNativeSsoStorage = (): NativeSsoStorageRepairResult => {
  try {
    const storage = getNativeStorage();
    const authToken = getAuthToken();
    const userRaw = storage.getItem(STORAGE.USER);

    if (userRaw && !authToken) {
      // User sans token = état partiel ou résiduel. On repart proprement.
      clearNativeSsoStorage();
      return { cleaned: true, reason: 'incomplete_session' };
    }

    if (authToken && !userRaw) {
      clearNativeSsoStorage();
      return { cleaned: true, reason: 'incomplete_session' };
    }

    if (userRaw) {
      try {
        JSON.parse(userRaw);
      } catch {
        clearNativeSsoStorage();
        return { cleaned: true, reason: 'invalid_user_json' };
      }
    }

    return { cleaned: false, reason: null };
  } catch (error) {
    if (isDebugMode()) {
      console.warn('⚠️ [native-sso] Storage repair failed, forcing cleanup', error);
    }
    try {
      clearNativeSsoStorage();
    } catch {
      // final fallback: do not throw
    }
    return { cleaned: true, reason: 'incomplete_session' };
  }
};

/**
 * Déconnexion complète et sécurisée.
 * 
 * Effectue la double revocation (SaaS + IAM) puis nettoie le storage.
 * **Toute déconnexion frontend DOIT passer par cette fonction** pour éviter
 * les sessions orphelines sur l'IAM.
 * 
 * @example
 * ```tsx
 * import { logout } from '@ollaid/native-sso';
 * 
 * const handleLogout = async () => {
 *   await logout();
 *   navigate('/auth/login');
 * };
 * ```
 */
export const logout = async (): Promise<{ success: boolean }> => {
  // Import dynamique pour éviter les dépendances circulaires
  const { nativeAuthService } = await import('./nativeAuth');
  const token = getAuthToken();
  return nativeAuthService.logout(token || undefined);
};

export const setAuthUser = (user: unknown): void => {
  getNativeStorage().setItem(STORAGE.USER, safeJSONStringify(user));
};

export const getAuthUser = <T>(): T | null => {
  const user = getNativeStorage().getItem(STORAGE.USER);
  if (!user) return null;
  try {
    return JSON.parse(user);
  } catch {
    getNativeStorage().removeItem(STORAGE.USER);
    return null;
  }
};

export const setAccountType = (type: string): void => {
  if (typeof type !== 'string' || type.length === 0) return;
  getNativeStorage().setItem(STORAGE.REGISTRATION_MODE, type);
};

export const getAccountType = (): string | null => {
  return getNativeStorage().getItem(STORAGE.REGISTRATION_MODE);
};

export function getSsoSessionSnapshot<TUser = unknown>(): SsoSessionSnapshot<TUser> {
  const storage = getNativeStorage();
  const userRaw = storage.getItem(STORAGE.USER);
  let user: TUser | null = null;

  if (userRaw) {
    try {
      user = JSON.parse(userRaw) as TUser;
    } catch {
      user = null;
    }
  }

  return {
    authToken: getAuthToken(),
    refreshToken: storage.getItem(STORAGE.REFRESH_TOKEN),
    refreshExpiresAt: storage.getItem(STORAGE.REFRESH_EXPIRES_AT),
    tokenExpiresAt: storage.getItem(STORAGE.TOKEN_EXPIRES_AT),
    iamToken: getAuthToken(),
    aliasReference: storage.getItem(STORAGE.ALIAS_REFERENCE),
    accountType: storage.getItem(STORAGE.REGISTRATION_MODE),
    deviceId: storage.getItem(DEVICE_ID_KEY),
    sessionUuid: storage.getItem(SESSION_UUID_KEY),
    user,
  };
}

export function hasSsoSession(): boolean {
  const snapshot = getSsoSessionSnapshot();
  return Boolean(snapshot.authToken && snapshot.user);
}

export { STORAGE as STORAGE_KEYS };
export { PROFILE_STORAGE as PROFILE_PROMPT_KEYS };

export interface ProfilePromptState {
  lastStatus: boolean | null;
  lastCheckAt: number | null;
  recheckAt: number | null;
}

export function getProfilePromptState(): ProfilePromptState {
  const storage = getNativeStorage();
  const lastStatusRaw = storage.getItem(PROFILE_STORAGE.IMAGE_LAST_STATUS);
  const lastCheckRaw = storage.getItem(PROFILE_STORAGE.IMAGE_LAST_CHECK);
  const recheckRaw = storage.getItem(PROFILE_STORAGE.IMAGE_RECHECK_AT);

  const parseTs = (value: string | null): number | null => {
    if (!value) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  return {
    lastStatus: lastStatusRaw === null ? null : lastStatusRaw === 'true',
    lastCheckAt: parseTs(lastCheckRaw),
    recheckAt: parseTs(recheckRaw),
  };
}

export function setProfilePromptState(status: boolean, recheckAt: number | null = null): void {
  const storage = getNativeStorage();
  storage.setItem(PROFILE_STORAGE.IMAGE_LAST_STATUS, status ? 'true' : 'false');
  storage.setItem(PROFILE_STORAGE.IMAGE_LAST_CHECK, String(Date.now()));

  if (recheckAt && Number.isFinite(recheckAt)) {
    storage.setItem(PROFILE_STORAGE.IMAGE_RECHECK_AT, String(recheckAt));
  } else {
    storage.removeItem(PROFILE_STORAGE.IMAGE_RECHECK_AT);
  }
}

export function markProfilePromptComplete(): void {
  setProfilePromptState(true, null);
}

export function snoozeProfilePrompt(hours = 8): void {
  setProfilePromptState(false, Date.now() + hours * 60 * 60 * 1000);
}

// ============================================
// Fetch helper
// ============================================

export async function fetchWithTimeout<T>(
  url: string,
  options: RequestInit,
  timeout: number
): Promise<T> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  const method = (options.method as 'GET' | 'POST' | 'PUT' | 'DELETE') || 'GET';
  let parsedBody: unknown = undefined;
  if (typeof options.body === 'string') {
    try {
      parsedBody = JSON.parse(options.body);
    } catch {
      parsedBody = options.body;
    }
  }
  const logId = isDebugMode() ? logApiCall(method, url, parsedBody) : null;
  const startTime = Date.now();

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    const contentType = response.headers.get('content-type');
    if (!contentType?.includes('application/json')) {
      await response.text();
      if (isDebugMode()) {
        console.error('❌ Réponse non-JSON:', response.status);
        const duration = Date.now() - startTime;
        if (logId) logApiResponse(logId, response.status, { raw: '[non-json response]' }, startTime, 'Non-JSON response');
        logDebugApiResponse(method, url, response.status, { raw: '[non-json response]' }, duration, parsedBody, 'Non-JSON response');
      }
      throw new ApiError(`Erreur serveur (${response.status})`, 'server', response.status);
    }

    const data = await response.json();
    const duration = Date.now() - startTime;

    if (!response.ok) {
      if (logId) logApiResponse(logId, response.status, data, startTime, data.message);
      logDebugApiResponse(method, url, response.status, data, duration, parsedBody, data.message);
      throw new ApiError(
        data.message || `Erreur ${response.status}`,
        response.status === 401 ? 'auth' : response.status >= 500 ? 'server' : 'validation',
        response.status,
        data.error_type,
        data
      );
    }

    if (logId) logApiResponse(logId, response.status, data, startTime);
    logDebugApiResponse(method, url, response.status, data, duration, parsedBody);
    return data;
  } catch (error) {
    clearTimeout(timeoutId);

    if (error instanceof ApiError) throw error;

    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        if (logId) logApiResponse(logId, 0, null, startTime, 'Timeout');
        logDebugApiResponse(method, url, 0, null, Date.now() - startTime, parsedBody, 'Timeout');
        throw new ApiError('Délai d\'attente dépassé', 'timeout');
      }
      if (error instanceof TypeError) {
        if (logId) logApiResponse(logId, 0, null, startTime, 'Network error');
        logDebugApiResponse(method, url, 0, null, Date.now() - startTime, parsedBody, 'Network error');
        throw new ApiError('Impossible de contacter le serveur', 'network');
      }
    }

    if (logId) logApiResponse(logId, 0, null, startTime, 'Unknown error');
    logDebugApiResponse(method, url, 0, null, Date.now() - startTime, parsedBody, 'Unknown error');
    throw new ApiError('Erreur inattendue', 'unknown');
  }
}

export function getHeaders(token?: string, includeConfigPrefix = false): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  };

  const deviceId = getDeviceId();
  if (deviceId) {
    headers['X-Device-Id'] = deviceId;
  }

  const sessionUuid = getSessionUuid();
  if (sessionUuid) {
    headers['X-Session-UUID'] = sessionUuid;
  }

  if (deviceContext?.label) {
    headers['X-Device-Label'] = deviceContext.label;
  }
  if (deviceContext?.browser) {
    headers['X-Device-Browser'] = deviceContext.browser;
  }
  if (deviceContext?.deviceName) {
    headers['X-Device-Name'] = deviceContext.deviceName;
  }
  if (deviceContext?.deviceModel) {
    headers['X-Device-Model'] = deviceContext.deviceModel;
  }
  if (deviceContext?.deviceManufacturer) {
    headers['X-Device-Manufacturer'] = deviceContext.deviceManufacturer;
  }
  if (deviceContext?.operatingSystem) {
    headers['X-Device-Operating-System'] = deviceContext.operatingSystem;
  }
  if (deviceContext?.osVersion) {
    headers['X-Device-Os-Version'] = deviceContext.osVersion;
  }
  if (deviceContext?.platform) {
    headers['X-Device-Platform'] = deviceContext.platform;
  }
  if (deviceContext?.language) {
    headers['X-Device-Language'] = deviceContext.language;
  }
  if (deviceContext?.webviewVersion) {
    headers['X-Device-WebView-Version'] = deviceContext.webviewVersion;
  }
  if (typeof deviceContext?.isNative === 'boolean') {
    headers['X-Device-Is-Native'] = deviceContext.isNative ? 'true' : 'false';
  }

  // Le prefix ne sert qu'aux appels SaaS qui choisissent une config locale.
  if (includeConfigPrefix && config.configPrefix) {
    headers['X-IAM-Config-Prefix'] = config.configPrefix;
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
}

export default {
  getConfig: getNativeAuthConfig,
  setConfig: setNativeAuthConfig,
};
